'use client';

import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, RocketLaunchIcon, SparklesIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { Button } from '@/components/ui/forms/button';
import type { SubscriptionPlanSummary } from '@/types/subscription';
import { PRICING_COPY } from '@/components/features/pricing/pricingCopy';
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';
import { getStoredPromoCode, clearStoredPromoCode } from '@/components/providers/PromoCodeCapture';

type PromoCodeState = {
  code: string;
  validation: { valid: boolean; name?: string; trialDays?: number; reason?: string } | null;
  validating: boolean;
};

type PlanCardsProps = {
  plans: SubscriptionPlanSummary[];
  mode?: 'pricing' | 'paywall';
  isProcessingPlanSlug?: string | null;
  onSubscribe?: (plan: SubscriptionPlanSummary, promoCode?: string) => void;
  ctaLabel?: string;
  processingLabel?: string;
};

const formatPrice = (plan: SubscriptionPlanSummary) => {
  if (plan.is_free) {
    return { price: '$0', cadence: 'forever' };
  }
  const cents = plan.billing_price_cents ?? 0;
  if (plan.billing_type === 'ANNUAL') {
    const monthlyEquivalent = (Math.round(cents / 12 / 50) * 50 / 100).toFixed(2);
    return { price: `$${monthlyEquivalent}`, cadence: 'per month' };
  }
  const price = `$${(cents / 100).toFixed(0)}`;
  const cadence =
    plan.billing_type === 'MONTHLY'
      ? 'per month'
      : plan.billing_type === 'QUARTERLY'
        ? 'per quarter'
        : null;
  return { price, cadence };
};

const resolveFeaturedSlug = (plans: SubscriptionPlanSummary[]) => {
  const yearlyPlan = plans.find((plan) => plan.billing_type === 'ANNUAL');
  if (yearlyPlan) return yearlyPlan.slug;
  const paidPlans = plans.filter((plan) => !plan.is_free && (plan.billing_price_cents ?? 0) > 0);
  const sorted = [...paidPlans].sort((a, b) => (b.billing_price_cents ?? 0) - (a.billing_price_cents ?? 0));
  return sorted[0]?.slug;
};

export function SubscriptionPlanCards({
  plans,
  mode = 'pricing',
  isProcessingPlanSlug,
  onSubscribe,
  ctaLabel,
  processingLabel,
}: PlanCardsProps) {
  const { trackEvent } = useEventTracking();
  const paidPlans = plans.filter((plan) => !plan.is_free);
  const featuredSlug = resolveFeaturedSlug(paidPlans);
  const orderedPlans = (() => {
    // Pricing page passes the free plan through so it can render as the
    // first card in the row — free, then paid plans cheapest to priciest,
    // matching the landing page's plan order. Callers that don't include a
    // free plan (paywall, settings resubscribe) keep the original
    // featured-plan-first ordering untouched.
    const freePlan = mode === 'pricing' ? plans.find((plan) => plan.is_free) : undefined;
    if (freePlan) {
      const paidByPrice = [...paidPlans].sort(
        (a, b) => (a.billing_price_cents ?? 0) - (b.billing_price_cents ?? 0)
      );
      return [freePlan, ...paidByPrice];
    }
    const featured = paidPlans.find((plan) => plan.slug === featuredSlug);
    const others = paidPlans.filter((plan) => plan.slug !== featuredSlug);
    if (!featured) return paidPlans;
    return [featured, ...others];
  })();

  // Shared promo code state across all plan cards
  const [promoExpanded, setPromoExpanded] = useState(false);
  const [promo, setPromo] = useState<PromoCodeState>({ code: '', validation: null, validating: false });
  const [storedHint, setStoredHint] = useState<string | null>(null);

  const validatePromoCode = useCallback(async (code: string) => {
    if (!code.trim()) {
      setPromo((p) => ({ ...p, validation: null }));
      return;
    }
    setPromo((p) => ({ ...p, validating: true }));
    try {
      const response = await fetch('/api/subscription/promo-code/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      const result = await response.json();
      setPromo((p) => ({ ...p, validation: result, validating: false }));
      if (!result.valid) clearStoredPromoCode();
    } catch {
      setPromo((p) => ({ ...p, validation: { valid: false, reason: 'validation_error' }, validating: false }));
    }
  }, []);

  // Check localStorage for stored promo code — validate before showing
  useEffect(() => {
    const stored = getStoredPromoCode();
    if (!stored) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch('/api/subscription/promo-code/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: stored }),
        });
        const result = await response.json();
        if (cancelled) return;

        if (result.valid) {
          setStoredHint(stored);
          setPromoExpanded(true);
        } else {
          clearStoredPromoCode();
        }
      } catch {
        // On network error, still show the hint — it'll be validated again on apply
        if (!cancelled) {
          setStoredHint(stored);
          setPromoExpanded(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const applyStoredPromo = useCallback(() => {
    if (!storedHint) return;
    setPromo((p) => ({ ...p, code: storedHint }));
    setStoredHint(null);
    validatePromoCode(storedHint);
  }, [storedHint, validatePromoCode]);

  const dismissStoredPromo = useCallback(() => {
    setStoredHint(null);
    clearStoredPromoCode();
  }, []);
  const gridCols =
    mode === 'pricing'
      ? orderedPlans.length === 2
        ? 'lg:grid-cols-2'
        : 'lg:grid-cols-3'
      : 'lg:grid-cols-2';
  const gridGap = 'gap-4';
  // Card padding matches the landing page's plan cards (28px 26px) on the
  // pricing page grid; the paywall dialog keeps a more compact padding since
  // it renders inside a narrower modal.
  const cardPadding = mode === 'paywall' ? 'p-3 sm:p-4' : 'px-[26px] py-7';
  const highlightLimit = Infinity;

  return (
    <div className={`grid grid-cols-1 ${gridCols} ${gridGap}`}>
      {orderedPlans.map((plan, index) => {
        const { price, cadence } = formatPrice(plan);
        const isAnnualPlan =
          plan.billing_type === 'ANNUAL' && !plan.is_free && (plan.billing_price_cents ?? 0) > 0;
        const annualTotalPrice =
          isAnnualPlan ? `$${Math.round((plan.billing_price_cents ?? 0) / 100)}` : null;
        const isMonthlyPlan =
          plan.billing_type === 'MONTHLY' && !plan.is_free && (plan.billing_price_cents ?? 0) > 0;
        const isFeatured = plan.slug === featuredSlug;
        const highlights = (plan.highlights || []).slice(0, highlightLimit);
        const isProcessing = isProcessingPlanSlug === plan.slug;

        return (
          <motion.div
            key={plan.slug}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 + index * 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative"
          >
            <div
              className={`relative h-full flex flex-col ${cardPadding} transition-transform duration-100 hover:-translate-y-px text-[#1b1b1b] ${
                mode === 'paywall' ? 'lg:max-h-[62vh]' : ''
              } ${
                isFeatured
                  ? 'border-2 border-[#b65252] bg-[#fff8f5]'
                  : 'border border-[#d7d0c6] bg-[#faf9f6]'
              }`}
              style={{
                boxShadow: isFeatured ? 'var(--shadow-warm-lg)' : 'var(--shadow-warm-md)',
              }}
            >
              {isFeatured && !plan.is_free && (
                <span className="absolute right-[18px] top-[18px] font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-[#7f1d1d]">
                  Most popular
                </span>
              )}

              <div
                className={
                  mode === 'paywall'
                    ? 'min-h-0 flex-1 overflow-y-visible lg:overflow-y-scroll lg:pr-3 scrollbar-burgundy scrollbar-rail-always [scrollbar-gutter:stable]'
                    : ''
                }
              >
                <h2 className="text-[15px] font-[750] leading-none text-[#1b1b1b]">
                  {plan.name}
                </h2>

                <div>
                  <p className="mt-[22px] text-[38px] font-extrabold leading-none tracking-[-0.045em] text-[#1b1b1b]">
                    {price}
                  </p>
                  {cadence && (
                    <p className="mt-2 text-[12px] text-[#615d57]">
                      {cadence}
                    </p>
                  )}
                  {annualTotalPrice && (
                    <div className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-md border border-border bg-primary/10 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-foreground">
                      {annualTotalPrice} billed annually
                    </div>
                  )}
                  {isMonthlyPlan && (
                    <div className="mt-2 inline-flex flex-wrap items-center gap-2 rounded-md border border-border/60 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      ${Math.round(((plan.billing_price_cents ?? 0) * 12) / 100)}/year
                      <span className="text-xs font-semibold">(billed monthly)</span>
                    </div>
                  )}
                  {plan.description && (
                    <p className={mode === 'paywall' ? 'mt-[22px] text-xs sm:text-sm text-[#57534e]' : 'mt-[22px] text-sm leading-[1.55] text-[#57534e]'}>
                      {plan.description}
                    </p>
                  )}
                </div>

                {highlights.length > 0 && (
                  <ul className="list-none grid gap-[11px] mt-6 pt-5 border-t border-t-[#dedad3]">
                    {highlights.map((feature, featureIndex) => (
                      <motion.li
                        key={`${plan.slug}-feature-${featureIndex}`}
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.15,
                          delay: 0.15 + index * 0.05 + featureIndex * 0.03,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                        className="grid grid-cols-[16px_minmax(0,1fr)] gap-[9px] text-[13px] leading-[1.45] text-[#3f3a36]"
                      >
                        <span aria-hidden="true" className="font-extrabold text-[#7f1d1d]">
                          ✓
                        </span>
                        <span>{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </div>

              <div className={`mt-auto ${mode === 'paywall' ? 'relative space-y-1.5 lg:border-t lg:border-border lg:pt-3' : 'space-y-2'}`}>
                {mode === 'paywall' ? (
                  <>
                    <div className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-b from-transparent to-card" />
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-border" />
                  </>
                ) : null}

                {/* Collapsible promo code section — not applicable to the free plan */}
                {!plan.is_free && (
                  <div className={mode === 'paywall' ? 'mb-1.5' : 'mb-2'}>
                    {!promoExpanded && !promo.validation?.valid ? (
                      <button
                        onClick={() => setPromoExpanded(true)}
                        className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ChevronDownIcon className="w-3.5 h-3.5 stroke-[2.5px]" />
                        I have a promo code
                      </button>
                    ) : (
                      <div className="space-y-1.5">
                        {promo.validation?.valid ? (
                          <div className="flex items-center gap-2 border border-green-600/30 bg-green-50 px-2.5 py-1.5">
                            <CheckIcon className="w-3.5 h-3.5 text-green-600 stroke-[3px] flex-shrink-0" />
                            <span className="text-xs font-bold text-green-700">
                              {promo.validation.name || 'Promo code'} applied{promo.validation.trialDays ? ` — ${promo.validation.trialDays}-day free trial` : ''}
                            </span>
                          </div>
                        ) : (
                          <>
                            {storedHint && !promo.code ? (
                              <div className="flex items-center justify-between gap-2 border border-primary/20 bg-primary/5 px-2.5 py-1.5">
                                <span className="text-xs font-bold text-foreground">
                                  Promo code: <span className="font-mono tracking-wider">{storedHint}</span>
                                </span>
                                <div className="flex gap-1.5">
                                  <button onClick={applyStoredPromo} className="text-xs font-bold uppercase tracking-wider text-primary hover:text-primary/80">Apply</button>
                                  <button onClick={dismissStoredPromo} className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">Dismiss</button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex gap-1.5">
                                <input
                                  type="text"
                                  value={promo.code}
                                  onChange={(e) => setPromo((p) => ({ ...p, code: e.target.value.toUpperCase(), validation: null }))}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); validatePromoCode(promo.code); } }}
                                  placeholder="ENTER CODE"
                                  className="flex-1 border border-border bg-background px-2.5 py-1.5 text-xs font-mono font-bold uppercase tracking-wider text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary min-w-0"
                                />
                                <button
                                  onClick={() => validatePromoCode(promo.code)}
                                  disabled={!promo.code.trim() || promo.validating}
                                  className="border border-border bg-muted px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                >
                                  {promo.validating ? '...' : 'Apply'}
                                </button>
                              </div>
                            )}
                            {promo.validation && !promo.validation.valid && (
                              <p className="text-xs font-bold text-red-500">
                                {promo.validation.reason === 'expired' ? 'This code has expired'
                                  : promo.validation.reason === 'already_redeemed' ? 'This code has already been used'
                                  : 'Invalid promo code'}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  onClick={() => {
                    trackEvent({
                      event_type: EventType.CLICK,
                      element_id: 'subscription-plan-cta',
                      event_details: {
                        action: 'subscription_plan_clicked',
                        funnel: 'subscription_upgrade',
                        mode,
                        plan_slug: plan.slug,
                        plan_name: plan.name,
                        billing_type: plan.billing_type,
                        billing_price_cents: plan.billing_price_cents ?? 0,
                        is_free: Boolean(plan.is_free),
                        is_featured: isFeatured,
                      },
                    });
                    const effectivePromoCode = promo.validation?.valid
                      ? promo.code
                      : storedHint || undefined;
                    onSubscribe?.(plan, effectivePromoCode);
                  }}
                  disabled={isProcessing}
                  size="lg"
                  variant={isFeatured ? 'default' : 'outline'}
                  className={mode === 'paywall' ? 'w-full py-4 text-sm' : 'w-full py-5 text-sm'}
                  style={{ boxShadow: 'var(--shadow-warm-md)' }}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 mr-2 border border-current border-t-transparent animate-spin"></div>
                      {processingLabel ?? 'Processing...'}
                    </>
                  ) : (
                    <>
                      {isFeatured ? (
                        <RocketLaunchIcon className="w-5 h-5 mr-2 stroke-[2.5px]" />
                      ) : (
                        <SparklesIcon className="w-5 h-5 mr-2 stroke-[2.5px]" />
                      )}
                      {plan.is_free
                        ? PRICING_COPY.cta.freePlan
                        : (ctaLabel ??
                          (mode === 'paywall'
                            ? PRICING_COPY.cta.paywall
                            : PRICING_COPY.cta.pricing))}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
